var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { AXIS_TEXT_COLOR } from "../utils/chartConstants";
import { formatNumericValue, getChartDataset, parseNumericValue } from "../../../utils/chartData";
var DAY_ORDER = [
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
    "Domingo",
];
var TURNO_ORDER = ["Madrugada", "Manhã", "Tarde", "Noite"];
var MONTH_LABELS = {
    "1": "Jan",
    "2": "Fev",
    "3": "Mar",
    "4": "Abr",
    "5": "Mai",
    "6": "Jun",
    "7": "Jul",
    "8": "Ago",
    "9": "Set",
    "10": "Out",
    "11": "Nov",
    "12": "Dez",
};
var sortByPreferredOrder = function (values, preferredOrder) {
    return __spreadArray([], values, true).sort(function (a, b) {
        var aIndex = preferredOrder.indexOf(a);
        var bIndex = preferredOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1)
            return a.localeCompare(b);
        if (aIndex === -1)
            return 1;
        if (bIndex === -1)
            return -1;
        return aIndex - bIndex;
    });
};
var resolveHeatmapAxes = function (dataset) {
    if (dataset.every(function (item) { return item.ano !== undefined && item.mes_num !== undefined; })) {
        var xValues = sortByPreferredOrder(Array.from(new Set(dataset.map(function (item) { return String(item.mes_num); }))), Object.keys(MONTH_LABELS));
        var yValues = Array.from(new Set(dataset.map(function (item) { return String(item.ano); }))).sort();
        return {
            xKey: "mes_num",
            yKey: "ano",
            xValues: xValues,
            yValues: yValues,
            xLabelFormatter: function (value) { var _a; return (_a = MONTH_LABELS[value]) !== null && _a !== void 0 ? _a : value; },
            yLabelFormatter: function (value) { return value; },
        };
    }
    if (dataset.every(function (item) { return item.turno !== undefined && item.dia_semana !== undefined; })) {
        return {
            xKey: "turno",
            yKey: "dia_semana",
            xValues: sortByPreferredOrder(Array.from(new Set(dataset.map(function (item) { return String(item.turno); }))), TURNO_ORDER),
            yValues: sortByPreferredOrder(Array.from(new Set(dataset.map(function (item) { return String(item.dia_semana); }))), DAY_ORDER),
            xLabelFormatter: function (value) { return value; },
            yLabelFormatter: function (value) { return value; },
        };
    }
    return {
        xKey: "faixa_2h",
        yKey: "dia_semana",
        xValues: Array.from(new Set(dataset.map(function (item) { return String(item.faixa_2h); }))).sort(),
        yValues: sortByPreferredOrder(Array.from(new Set(dataset.map(function (item) { return String(item.dia_semana); }))), DAY_ORDER),
        xLabelFormatter: function (value) { return "".concat(value, "h"); },
        yLabelFormatter: function (value) { return value; },
    };
};
export function buildHeatmapOption(data, isMobile) {
    var dataset = getChartDataset(data);
    var _a = resolveHeatmapAxes(dataset), xKey = _a.xKey, yKey = _a.yKey, xValues = _a.xValues, yValues = _a.yValues, xLabelFormatter = _a.xLabelFormatter, yLabelFormatter = _a.yLabelFormatter;
    var xIndexByValue = new Map(xValues.map(function (value, index) { return [value, index]; }));
    var yIndexByValue = new Map(yValues.map(function (value, index) { return [value, index]; }));
    var maxValue = dataset.reduce(function (currentMax, item) { var _a; return Math.max(currentMax, parseNumericValue((_a = item.total) !== null && _a !== void 0 ? _a : item.value)); }, 0);
    return {
        tooltip: {
            trigger: "item",
            backgroundColor: "rgba(255,255,255,0.96)",
            borderColor: "#ECEEF4",
            borderWidth: 1,
            textStyle: {
                fontFamily: "Montserrat, sans-serif",
                color: "#374151",
                fontSize: 13,
            },
            extraCssText: "box-shadow: 0 4px 20px rgba(0,0,0,0.08); border-radius: 10px;",
            formatter: function (params) {
                var _a, _b, _c;
                var rawData = (_a = params.data) !== null && _a !== void 0 ? _a : [0, 0, 0];
                var xLabel = xLabelFormatter((_b = xValues[rawData[0]]) !== null && _b !== void 0 ? _b : "");
                var yLabel = yLabelFormatter((_c = yValues[rawData[1]]) !== null && _c !== void 0 ? _c : "");
                return "<strong>".concat(yLabel, "</strong><br/>").concat(xLabel, ": <strong>").concat(formatNumericValue(rawData[2]), "</strong>");
            },
        },
        grid: {
            left: isMobile ? "7%" : "5%",
            right: isMobile ? "9%" : "7%",
            top: "10%",
            bottom: isMobile ? "12%" : "8%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xValues.map(xLabelFormatter),
            splitArea: { show: false },
            axisLabel: {
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? 9 : 11,
                color: AXIS_TEXT_COLOR,
                interval: 0,
                rotate: isMobile && xValues.length > 6 ? 24 : 0,
            },
            axisLine: { lineStyle: { color: "#E5E7EB" } },
            axisTick: { show: false },
        },
        yAxis: {
            type: "category",
            data: yValues.map(yLabelFormatter),
            splitArea: { show: false },
            axisLabel: {
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? 9 : 11,
                color: AXIS_TEXT_COLOR,
            },
            axisLine: { lineStyle: { color: "#E5E7EB" } },
            axisTick: { show: false },
        },
        visualMap: {
            min: 0,
            max: maxValue || 1,
            calculable: true,
            orient: isMobile ? "horizontal" : "vertical",
            left: isMobile ? "center" : "right",
            bottom: isMobile ? 0 : "middle",
            itemWidth: isMobile ? 140 : 14,
            itemHeight: isMobile ? 10 : 160,
            textStyle: {
                fontFamily: "Montserrat, sans-serif",
                color: AXIS_TEXT_COLOR,
            },
            inRange: {
                color: ["#FDF2F8", "#F9A8D4", "#F472B6", "#EC4899", "#BE185D"],
            },
        },
        series: [
            {
                type: "heatmap",
                data: dataset
                    .map(function (item) {
                    var _a, _b, _c;
                    var xIndex = xIndexByValue.get(String((_a = item[xKey]) !== null && _a !== void 0 ? _a : ""));
                    var yIndex = yIndexByValue.get(String((_b = item[yKey]) !== null && _b !== void 0 ? _b : ""));
                    if (xIndex == null || yIndex == null)
                        return null;
                    return [xIndex, yIndex, parseNumericValue((_c = item.total) !== null && _c !== void 0 ? _c : item.value)];
                })
                    .filter(Boolean),
                label: {
                    show: true,
                    color: "#1F2937",
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: isMobile ? 9 : 10,
                    formatter: function (_a) {
                        var rawData = _a.data;
                        return rawData[2] > 0 ? formatNumericValue(rawData[2]) : "";
                    },
                },
                itemStyle: {
                    borderColor: "#FFFFFF",
                    borderWidth: 1,
                    borderRadius: 6,
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 16,
                        shadowColor: "rgba(0,0,0,0.15)",
                    },
                },
            },
        ],
    };
}
